import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CheckCircle } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const CreateOwnClinicPage = () => {
  const navigate = useNavigate();
  const { canCreateOwnClinic, clinicContextsLoading, createOwnClinic } = useAuth();
  const [clinicName, setClinicName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clinicContextsLoading && !canCreateOwnClinic && !saving) {
      navigate('/dashboard', { replace: true });
    }
  }, [canCreateOwnClinic, clinicContextsLoading, navigate, saving]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedName = clinicName.trim().replace(/\s+/g, ' ');
    if (normalizedName.length < 2) {
      setError('Informe o nome da sua clínica.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await createOwnClinic(normalizedName);
      navigate('/assinatura', { replace: true });
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
        'Não foi possível criar sua clínica. Tente novamente.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Criar minha clínica</h1>
              <p className="text-sm text-gray-600 mt-1">Use o mesmo login para administrar sua própria conta.</p>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-800">
                Seu acesso às clínicas que convidaram você será mantido. Depois, você poderá alternar entre elas pelo seletor de clínicas.
              </p>
            </div>
          </div>

          {error && (
            <div className="mt-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6">
            <label htmlFor="own-clinic-name" className="block text-sm font-medium text-gray-700">
              Nome da clínica
            </label>
            <input
              id="own-clinic-name"
              type="text"
              value={clinicName}
              onChange={(event) => setClinicName(event.target.value)}
              maxLength={120}
              autoFocus
              required
              placeholder="Ex.: Clínica Odontológica Sorriso"
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-purple-500 focus:ring-purple-500"
            />
            <p className="mt-2 text-xs text-gray-500">
              Você poderá completar os demais dados da clínica nas configurações.
            </p>

            <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={saving}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving || clinicContextsLoading}
                className="px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
              >
                {saving ? 'Criando clínica...' : 'Criar clínica e continuar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CreateOwnClinicPage;
